import { useState, useEffect, useCallback } from "react";
import { Search, X, Trash2, Upload, ListPlus, UserPlus } from "lucide-react";
import api from "../utils/api";
import { useToast } from "../components/Toast";
import { fmtDate } from "../utils/helpers";
import Card from "../components/Card";
import StatusBadge from "../components/StatusBadge";
import Pagination from "../components/Pagination";
import Modal from "../components/Modal";
import type { Recipient, List } from "../types";

const PAGE_SIZE = 25;

export default function RecipientsPage() {
  const { toast } = useToast();
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [genderFilter, setGenderFilter] = useState({ F: true, M: true, N: true });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  // Modals
  const [showCreateList, setShowCreateList] = useState(false);
  const [showAddToList, setShowAddToList] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newListDesc, setNewListDesc] = useState("");
  const [lists, setLists] = useState<List[]>([]);
  const [selectedListId, setSelectedListId] = useState<number | null>(null);

  const genderParam = () => {
    const all = genderFilter.F && genderFilter.M && genderFilter.N;
    const none = !genderFilter.F && !genderFilter.M && !genderFilter.N;
    if (all || none) return "";
    const parts: string[] = [];
    if (genderFilter.F) parts.push("F");
    if (genderFilter.M) parts.push("M");
    if (genderFilter.N) parts.push("null");
    return parts.join(",");
  };

  const fetchRecipients = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, page_size: PAGE_SIZE };
      if (activeSearch) params.search = activeSearch;
      const gp = genderParam();
      if (gp) params.gender = gp;
      const res = await api.get("/recipients", { params });
      if (res.data.ok) {
        setRecipients(res.data.recipients || []);
        setTotal(res.data.total || 0);
      }
    } catch {} finally { setLoading(false); }
  }, [page, activeSearch, genderFilter]);

  useEffect(() => { fetchRecipients(); }, [fetchRecipients]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const doSearch = () => { setActiveSearch(search); setPage(1); setSelected(new Set()); };
  const clearSearch = () => { setSearch(""); setActiveSearch(""); setPage(1); setSelected(new Set()); };

  const toggleGender = (g: "F" | "M" | "N") => {
    setGenderFilter((p) => ({ ...p, [g]: !p[g] }));
    setPage(1);
    setSelected(new Set());
  };

  // Selection
  const isAllPageSelected = recipients.length > 0 && recipients.every((r) => selected.has(String(r.id)));
  const isSomeSelected = recipients.some((r) => selected.has(String(r.id)));

  const toggleAll = () => {
    const s = new Set(selected);
    if (isAllPageSelected) { recipients.forEach((r) => s.delete(String(r.id))); }
    else { recipients.forEach((r) => s.add(String(r.id))); }
    setSelected(s);
  };

  const toggleOne = (id: number) => {
    const s = new Set(selected);
    const k = String(id);
    if (s.has(k)) s.delete(k); else s.add(k);
    setSelected(s);
  };

  const selectAllPages = async () => {
    try {
      const params: any = {};
      if (activeSearch) params.search = activeSearch;
      const gp = genderParam();
      if (gp) params.gender = gp;
      const res = await api.get("/recipients/ids", { params });
      if (res.data.ok && res.data.ids) {
        const s = new Set(selected);
        res.data.ids.forEach((id: number) => s.add(String(id)));
        setSelected(s);
        toast("Selected " + res.data.ids.length + " recipients", "info");
      }
    } catch {}
  };

  const clearAll = () => setSelected(new Set());

  const selectedIds = (): number[] => {
    return Array.from(selected).map((s) => parseInt(s, 10)).filter((n) => n > 0);
  };

  // Delete selected
  const deleteSelected = async () => {
    const ids = selectedIds();
    if (!ids.length) return;
    if (!confirm("Delete " + ids.length + " recipient(s)? This removes them from all lists and campaigns.")) return;
    try {
      const res = await api.delete("/recipients", { data: { ids } });
      if (res.data.ok) {
        toast("Deleted " + res.data.deleted + " recipients", "success");
        setSelected(new Set());
        fetchRecipients();
      } else toast(res.data.error || "Error", "error");
    } catch (err: any) { toast(err.response?.data?.error || "Error", "error"); }
  };

  // XLSX import
  const handleXlsxImport = async (file: File) => {
    try {
      const buffer = await file.arrayBuffer();
      const res = await api.post("/recipients/import/xlsx", buffer, {
        headers: { "Content-Type": "application/octet-stream" },
      });
      if (res.data.ok) {
        const skipped = (res.data.skipped_no_phone || 0) + (res.data.skipped_invalid || 0);
        toast("Inserted: " + (res.data.inserted || 0) + ", Updated: " + (res.data.updated || 0) + ", Skipped: " + skipped, "success");
        fetchRecipients();
      } else toast(res.data.error || "Import error", "error");
    } catch (err: any) { toast(err.response?.data?.error || "Import error", "error"); }
  };

  // Create list from selection
  const fetchLists = async () => {
    try {
      const res = await api.get("/lists");
      if (res.data.ok) setLists(res.data.lists || []);
    } catch {}
  };

  const createListFromSelection = async () => {
    if (!newListName.trim()) { toast("Name required", "error"); return; }
    try {
      const res = await api.post("/lists", { name: newListName.trim(), description: newListDesc.trim() || undefined });
      if (res.data.ok) {
        const listId = res.data.list.id;
        const ids = selectedIds();
        if (ids.length) {
          await api.post("/lists/" + listId + "/members/import", { recipient_ids: ids });
        }
        toast("List created with " + ids.length + " members", "success");
        setShowCreateList(false);
        setNewListName(""); setNewListDesc("");
      } else toast(res.data.error || "Error", "error");
    } catch (err: any) { toast(err.response?.data?.error || "Error", "error"); }
  };

  const addToList = async () => {
    if (!selectedListId) return;
    try {
      const ids = selectedIds();
      const res = await api.post("/lists/" + selectedListId + "/members/import", { recipient_ids: ids });
      if (res.data.ok) {
        toast("Added " + (res.data.inserted || 0) + " members", "success");
        setShowAddToList(false);
      } else toast(res.data.error || "Error", "error");
    } catch (err: any) { toast(err.response?.data?.error || "Error", "error"); }
  };

  const openAddToList = () => { fetchLists(); setShowAddToList(true); };
  const openCreateList = () => { setShowCreateList(true); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Recipients</h1>
          <p className="text-zinc-400 text-sm mt-1">{total} total recipients</p>
        </div>
      </div>

      <Card title="Recipients" badge={<span className="text-xs text-zinc-500">{total}</span>}>
        {/* Search & Filter */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex gap-2 flex-1 min-w-[200px]">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              placeholder="Search phone/name..."
              className="flex-1 bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-fuchsia-500 transition-colors"
            />
            <button onClick={doSearch} className="px-3 py-2 rounded-xl bg-fuchsia-600 text-white hover:bg-fuchsia-500 transition-colors">
              <Search size={16} />
            </button>
            {activeSearch && (
              <button onClick={clearSearch} className="px-3 py-2 rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors">
                <X size={16} />
              </button>
            )}
          </div>

          {/* Gender filters */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">Gender:</span>
            {(["F", "M", "N"] as const).map((g) => (
              <button
                key={g}
                onClick={() => toggleGender(g)}
                className={"px-2.5 py-1 text-xs rounded-lg font-medium transition-colors " +
                  (genderFilter[g]
                    ? g === "F" ? "bg-pink-500/20 text-pink-400" : g === "M" ? "bg-blue-500/20 text-blue-400" : "bg-zinc-500/20 text-zinc-400"
                    : "bg-zinc-900 text-zinc-600"
                  )
                }
              >
                {g === "N" ? "N/D" : g}
              </button>
            ))}
          </div>
        </div>

        {/* Selection actions */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-400">Selected: <span className="text-white font-semibold">{selected.size}</span></span>
            <button onClick={selectAllPages} className="text-xs px-2 py-1 rounded bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors">
              Select all {total}
            </button>
            {selected.size > 0 && (
              <button onClick={clearAll} className="text-xs px-2 py-1 rounded bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors">
                Clear all
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={openCreateList} disabled={selected.size === 0} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ListPlus size={14} /> Create list
            </button>
            <button onClick={openAddToList} disabled={selected.size === 0} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <UserPlus size={14} /> Add to list
            </button>
            <button onClick={deleteSelected} disabled={selected.size === 0} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <Trash2 size={14} /> Delete selected
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-zinc-500 text-left border-b border-zinc-800">
                <th className="pb-3 w-8">
                  <input type="checkbox" checked={isAllPageSelected} onChange={toggleAll}
                    className="rounded border-zinc-600 bg-zinc-900 text-fuchsia-500 focus:ring-fuchsia-500" />
                </th>
                <th className="pb-3 font-medium">Phone</th>
                <th className="pb-3 font-medium">Name</th>
                <th className="pb-3 font-medium w-16">Gender</th>
                <th className="pb-3 font-medium w-24">Birthday</th>
                <th className="pb-3 font-medium w-24">Registered</th>
                <th className="pb-3 font-medium w-20">Opted out</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center text-zinc-600 py-8">Loading...</td></tr>
              ) : recipients.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-zinc-600 py-8">No recipients</td></tr>
              ) : recipients.map((r) => (
                <tr key={r.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <td className="py-2.5">
                    <input type="checkbox" checked={selected.has(String(r.id))} onChange={() => toggleOne(r.id)}
                      className="rounded border-zinc-600 bg-zinc-900 text-fuchsia-500 focus:ring-fuchsia-500" />
                  </td>
                  <td className="py-2.5 font-mono text-xs">{r.phone}</td>
                  <td className="py-2.5">{[r.name, r.last_name].filter(Boolean).join(" ") || "—"}</td>
                  <td className="py-2.5">{r.gender ? <StatusBadge status={r.gender} /> : <span className="text-zinc-600">—</span>}</td>
                  <td className="py-2.5 text-xs text-zinc-400">{fmtDate(r.birthday)}</td>
                  <td className="py-2.5 text-xs text-zinc-400">{fmtDate(r.registration_date)}</td>
                  <td className="py-2.5"><StatusBadge status={r.opted_out ? "yes" : "no"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination page={page} totalPages={totalPages} total={total} onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => p + 1)} />

        {/* XLSX Import */}
        <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center gap-3">
          <Upload size={16} className="text-zinc-400" />
          <span className="text-sm text-zinc-400">Import from Excel (.xlsx):</span>
          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleXlsxImport(file);
              e.target.value = "";
            }}
            className="text-sm text-zinc-400 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-zinc-800 file:text-zinc-300 file:text-sm file:cursor-pointer hover:file:bg-zinc-700"
          />
        </div>
      </Card>

      {/* Create List Modal */}
      <Modal open={showCreateList} onClose={() => setShowCreateList(false)} title="Create List from Selection">
        <div className="space-y-4">
          <p className="text-sm text-zinc-400">{selected.size} recipients selected</p>
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">List name</label>
            <input value={newListName} onChange={(e) => setNewListName(e.target.value)} placeholder="e.g. VIP Clients"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-fuchsia-500 transition-colors" />
          </div>
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Description (optional)</label>
            <input value={newListDesc} onChange={(e) => setNewListDesc(e.target.value)} placeholder="optional"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-fuchsia-500 transition-colors" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setShowCreateList(false)} className="px-4 py-2 rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors text-sm">Cancel</button>
            <button onClick={createListFromSelection} className="px-4 py-2 rounded-xl bg-fuchsia-600 text-white hover:bg-fuchsia-500 transition-colors text-sm">Create</button>
          </div>
        </div>
      </Modal>

      {/* Add to List Modal */}
      <Modal open={showAddToList} onClose={() => setShowAddToList(false)} title="Add to List">
        <div className="space-y-4">
          <p className="text-sm text-zinc-400">{selected.size} recipients selected. Opted-out will be skipped.</p>
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Select list</label>
            <select
              value={selectedListId || ""}
              onChange={(e) => setSelectedListId(parseInt(e.target.value, 10) || null)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-fuchsia-500 transition-colors"
            >
              <option value="">-- Select --</option>
              {lists.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.member_count} members)</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setShowAddToList(false)} className="px-4 py-2 rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors text-sm">Cancel</button>
            <button onClick={addToList} disabled={!selectedListId} className="px-4 py-2 rounded-xl bg-fuchsia-600 text-white hover:bg-fuchsia-500 disabled:opacity-40 transition-colors text-sm">Add</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
